import type { Model } from '../types/api.types';

/**
 * Enhanced model selection with robust fallback logic
 *
 * Fallback Hierarchy:
 * 1. User's preferred default model (even if not in available models - trust backend)
 * 2. Model marked as isDefault in the system
 * 3. First available model in the list
 */
export function selectDefaultModel(
  models: Model[],
  userDefaultModel?:
    | Model
    | { modelName: string; name?: string; modelProvider?: string }
    | null
): Model | undefined {
  if (!models || models.length === 0) {
    // If no models available but user has a default, return it
    if (userDefaultModel && 'modelName' in userDefaultModel) {
      return userDefaultModel as Model;
    }
    return undefined;
  }

  let defaultModel: Model | undefined;

  // First try to use the user's default_model if it exists
  if (userDefaultModel?.modelName) {
    // Check if it's in the available models
    defaultModel = models.find(m => m.modelName === userDefaultModel.modelName);

    // If not found in available models, but we have the full object from backend, use it
    if (!defaultModel && 'name' in userDefaultModel && userDefaultModel.name) {
      return userDefaultModel as Model;
    }
  }

  // If no user default model, try to find model marked as isDefault
  if (!defaultModel) {
    defaultModel = models.find(m => m.isDefault === true);
    if (defaultModel) {
      return defaultModel;
    }
  }

  // If no system default model, use the first available model
  if (!defaultModel && models.length > 0) {
    defaultModel = models[0];
  }

  return defaultModel;
}

/**
 * Check if a model is the Gemini Flash model
 */
export function isGeminiFlash(model: Model): boolean {
  return (
    model.modelName === 'gemini-2.5-flash' ||
    model.modelName === 'gemini-2.0-flash-001'
  );
}

/**
 * Get model description for display
 */
export function getModelDescription(modelName: string): string {
  const descriptionMap: Record<string, string> = {
    'Gemini 2.0 Flash': '🤖️ Great for smart responses and deeper reasoning',
    'Gemini 2.5 Flash': '⚡️ Fast replies for simple tasks and quick answers',
    'Claude 3 Opus': '🧠️ Most capable model for complex tasks',
    'Claude 4.0 Sonnet': '🧠️ Best for complex thinking and long-form content',
    'Claude 3.7 Sonnet': '🧠️ Best for complex thinking and long-form content',
    'Claude 3.5 Sonnet': '🧠️ Balanced model for most tasks',
    'GPT-4o': '🔍️ Balanced option for everyday tasks and reliability',
    'GPT-4': '🔍️ Advanced reasoning and problem-solving',
  };
  return descriptionMap[modelName] || '';
}

/**
 * Get model icon path based on provider
 */
export function getModelIconPath(modelProvider: string): string | null {
  const iconMap: Record<string, string> = {
    google: '/assets/gemini.svg',
    anthropic: '/assets/claude.svg',
    openai: '/assets/openai.svg',
  };

  return iconMap[modelProvider] || null;
}
